<script setup>
import { useI18n } from "vue-i18n";

const config = useRuntimeConfig()
const i18n = useI18n()
const route = useRoute()

// Basis-Daten
const articleSlug = route.params.id
const lang = i18n.locale.value

//https://api.storyblok.com/v2/cdn/stories/posts/beeinflusst-die-dauer-des-rauchens-den-entzug?version=draft&resolve_relations=category%2Cauthor%2Creviewer&language=default&token=ZKVz5vYrHivRPiqP6HcOdwtt&cv=1710835609&resolve_level=2

console.log('Loading article:', articleSlug)

// ✅ Direkter useAsyncStoryblok-Call
const storyData = await useAsyncStoryblok(`posts/${articleSlug}`, {
  version: (config.public.blog?.mode || 'published'),
  resolve_relations: 'category,author,reviewer',
  language: lang === 'en' ? 'default' : lang
})

console.log('Storyblok data:', storyData.value)

// ✅ Statische Datenverarbeitung - nur auf Server
const story = storyData.value
const rels = story?.rels || []

if (!story) {
  if (import.meta.server) {
    setResponseStatus(404, 'Article not found')
  }

  useHead({
    title: 'Article not found | Smoxy',
    meta: [
      { name: 'robots', content: 'noindex,nofollow' },
    ],
  })
}

// Author und Reviewer direkt finden
const author = rels.find((x) => x.uuid === story?.content.author)
const reviewer = rels.find((x) => x.uuid === story?.content.reviewer)

// Categories filtern
const categories = story?.content.category 
  ? story.content.category
      .filter((id) => id !== config.public.blog?.categoryUUID)
      .map((id) => rels.find((x) => x.uuid === id))
      .filter(Boolean)
  : []

// Paths für Navigation
const deSlug = story?.translated_slugs?.find((x) => x.lang === 'de')?.path.split('/').pop() || ''
const paths = {
  enPath: `/posts/${story?.slug || ''}`,
  dePath: deSlug ? `/de/posts/${deSlug}` : undefined
}

// Route definition
if (story && paths.enPath) {
  defineI18nRoute({
    paths: {
      en: paths.enPath,
      ...(paths.dePath ? { de: paths.dePath } : {}),
    },
  })
}

// SEO Meta-Daten
if (story) {
  const title = story.content.metatags?.title || story.content.title
  const description = story.content.metatags?.description || extractPlainText(story.content.copy)
  const image = story.content.image?.filename ? story.content.image.filename + '/m/' : ''
  const imageAlt = story.content.image?.alt || story.content.title || ''
  
  const baseUrl = config.public.hostname || 'http://localhost:3000'
  const enUrl = baseUrl + paths.enPath
  const deUrl = paths.dePath ? baseUrl + paths.dePath : undefined
  const currentUrl = lang === 'de' && deUrl ? deUrl : enUrl
  
  useServerSeoMeta({
    title: title,
    description: description,
    keywords: story.content.keywords || '',
    author: author?.name || 'Smoxy',
    ogTitle: title,
    ogDescription: description,
    ogImage: image,
    ogImageAlt: imageAlt,
    ogType: 'article',
    ogUrl: currentUrl,
    twitterCard: 'summary_large_image',
    twitterTitle: title,
    twitterDescription: description,
    twitterImage: image,
    robots: 'index,follow',
  })

  const alternateLinks = [
    { rel: 'canonical', href: currentUrl },
    { rel: 'alternate', href: enUrl, hreflang: 'x-default' },
    { rel: 'alternate', href: enUrl, hreflang: 'en' },
    { rel: 'alternate', href: enUrl, hreflang: 'en-US' },
    ...(deUrl ? [
      { rel: 'alternate', href: deUrl, hreflang: 'de' },
      { rel: 'alternate', href: deUrl, hreflang: 'de-DE' },
    ] : []),
  ]

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    url: currentUrl,
    inLanguage: lang === 'de' ? 'de-DE' : 'en-US',
    ...(image ? { image: [image] } : {}),
    ...(story.published_at ? { datePublished: story.published_at } : {}),
    ...(story.updated_at ? { dateModified: story.updated_at } : {}),
    author: author?.name ? {
      '@type': 'Person',
      name: author.name,
    } : {
      '@type': 'Organization',
      name: 'Smoxy',
      url: baseUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Smoxy',
      url: baseUrl,
      logo: {
        '@type': 'ImageObject',
        url: `${baseUrl}/android-chrome-512x512.png`,
      },
    },
  }

  useHead({
    link: alternateLinks,
    script: [{
      type: 'application/ld+json',
      innerHTML: JSON.stringify(articleSchema).replace(/</g, '\\u003c'),
    }],
  })
  
  console.log('Story loaded:', story.name)
  console.log('Author:', author?.name)
  console.log('Categories:', categories.length)
}
</script>

<template>
  <div class="overflow-x-hidden bg-newdark-800">
    <Header theme="dark" />
    
    <!-- Content -->
    <StoryblokComponent 
      v-if="story" 
      :blok="story.content" 
      :story="story" 
      :author="author" 
      :reviewer="reviewer" 
      :categories="categories"   
    />
    
    <!-- Footer -->
    <Footer 
      v-if="story" 
      theme="dark" 
      :en-slug="paths.enPath" 
      :de-slug="paths.dePath" 
    />
  </div>
</template>
